
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { useProgressShares } from "./useProgressShares";

export function useQuizProgressSync(resourceId?: string, fileId?: string) {
  const { session } = useSession();
  const { calculateProgress, updateResourceShares } = useProgressShares(resourceId);

  const syncQuizProgress = useCallback(async () => {
    if (!resourceId || !fileId || !session?.user?.id) {
      return Promise.reject(new Error('Missing required parameters'));
    }
    
    try {
      console.log(`Syncing quiz progress for file ${fileId} in resource ${resourceId}`);
      
      // Check if this is a quiz file
      const { data: fileData } = await supabase
        .from('resource_files')
        .select('file_type, quiz_id')
        .eq('id', fileId)
        .maybeSingle();
        
      if (!fileData || fileData.file_type !== 'quiz' || !fileData.quiz_id) {
        console.log('Not a quiz file or missing quiz_id, skipping sync');
        return Promise.resolve(false);
      }
      
      // Check for passed quiz attempts
      const { data: quizAttempt } = await supabase
        .from('quiz_attempts')
        .select('*')
        .eq('quiz_id', fileData.quiz_id)
        .eq('trainee_id', session.user.id)
        .eq('passed', true)
        .limit(1);
        
      if (quizAttempt && quizAttempt.length > 0) {
        console.log('Found passed quiz attempt, updating training progress');
        
        const now = new Date().toISOString();
        
        const { data: existingProgress } = await supabase
          .from('training_progress')
          .select('id, completed')
          .eq('resource_id', resourceId)
          .eq('file_id', fileId)
          .eq('user_id', session.user.id)
          .maybeSingle();
          
        if (existingProgress) {
          if (!existingProgress.completed) {
            const { error: updateError } = await supabase
              .from('training_progress')
              .update({
                completed: true,
                completed_at: now,
                updated_at: now
              })
              .eq('id', existingProgress.id);
              
            if (updateError) throw updateError;
            
            // Recalculate progress
            const calculatedProgress = await calculateProgress();
            await updateResourceShares(calculatedProgress);
            
            return Promise.resolve(true);
          }
        } else {
          const { error: insertError } = await supabase
            .from('training_progress')
            .insert({
              resource_id: resourceId,
              file_id: fileId,
              user_id: session.user.id,
              completed: true,
              completed_at: now,
              updated_at: now
            });
            
          if (insertError) throw insertError;
          
          // Recalculate progress
          const calculatedProgress = await calculateProgress();
          await updateResourceShares(calculatedProgress);
          
          return Promise.resolve(true);
        }
      }
      
      return Promise.resolve(false);
      
    } catch (error) {
      console.error('Error in syncQuizProgress:', error);
      return Promise.resolve(false);
    }
  }, [resourceId, fileId, session?.user?.id, calculateProgress, updateResourceShares]);

  return {
    syncQuizProgress
  };
}
